<template>
    <layout>
        <main class="attachment-page">
            <section class="header">
                <h2>4. Define Structure Attachment and Pipe Attachment</h2>
                <div v-if="!hasAgency && designId" class="required">
                    AGENCY SELECTION REQUIRED: Please go back to
                    <router-link :to="{ name: 'submittal-setup', params: {id: designId}}">Seismic Design Setup</router-link>
                    and select an agency before proceeding for accurate options here.
                </div>
            </section>
            <section class="attachment">
                <fieldset class="flex">
                    <div class="header">
                        <h3>Define the Structure</h3>
                    </div>
                    <div>
                        <label for="structure">
                            Structure
                        </label>
                        <div class="select">
                            <select id="structure" placeholder="Select Your Structure" v-model="values.structure" @change="updateOptions('structure')">
                                <option v-for="option in makeMenuOptions('structure')" :value="option.value" v-text="option.value" :disabled="option.disabled"></option>
                            </select>
                        </div>
                        <div class="text user-defined" v-if="isUserDefined(values.structure)">
                            <input type="text" id="structure-other" placeholder="Enter Your Structure" v-model="values.udStructure">
                        </div>
                    </div>
                    <div v-if="showSubstrateSection">
                        <label for="substrate">
                            Substrate
                        </label>
                        <div v-if="substrateOptions.length == 1" v-text="substrateOptions[0].value"></div>
                        <div v-else class="select">
                            <select id="substrate" placeholder="Select Your Substrate" v-model="values.substrate" @change="updateOptions('substrate')">
                                <option v-for="option in substrateOptions" :value="option.value" v-text="option.value" :disabled="option.disabled"></option>
                            </select>
                        </div>
                        <div class="text user-defined" v-if="isUserDefined(values.substrate)">
                            <input type="text" id="substrate-other" placeholder="Enter Your Substrate" v-model="values.udSubstrate">
                        </div>
                    </div>
                </fieldset>
                <fieldset v-if="showFastenerSection && options.fastener && options.fastener.length" class="flex fastener">
                    <div class="header">
                        <h3>Define the Fastener</h3>
                        <div class="help">
                            <i class="fa fa-question inline-popup" data-mfp-src="#fastener-help"></i>
                        </div>
                    </div>
                    <div>
                        <label for="anchor">
                            Fastener
                        </label>
                        <div v-if="fastenerOptions.length == 1" v-text="fastenerOptions[0].value"></div>
                        <div v-else class="select">
                            <select id="anchor" placeholder="Select Your Anchor" v-model="values.fastener" @change="updateOptions('fastener')">
                                <option v-for="option in makeMenuOptions('fastener')" :value="option.value" v-text="option.value" :disabled="option.disabled"></option>
                            </select>
                        </div>
                        <div class="text user-defined" v-if="isUserDefined(values.fastener)">
                            <input type="text" id="fastener-other" placeholder="Enter Fastener Name" v-model="values.udFastener">
                        </div>
                    </div>
                    <div v-if="options.fastenerOrientation && options.fastenerOrientation.length">
                        <label for="fastener-orientation">
                            Fastener Orientation
                        </label>
                        <div v-if="fastenerOrientationOptions.length == 1" v-text="fastenerOrientationOptions[0].value"></div>
                        <div v-else class="select">
                            <select id="fastener-orientation" placeholder="Select Your Fastener Orientation" v-model="values.fastenerOrientation" @change="updateOptions('fastenerOrientation')">
                                <option v-for="option in makeMenuOptions('fastenerOrientation')" :value="option.value" v-text="option.value" :disabled="option.disabled"></option>
                            </select>
                        </div>
                    </div>
                    <div v-if="options.fastenerSize && options.fastenerSize.length">
                        <label for="fastener-size">
                            Fastener Size
                        </label>
                        <div v-if="fastenerSizeOptions.length == 1" v-text="fastenerSizeOptions[0].value"></div>
                        <div v-else class="select">
                            <select id="fastener-size" placeholder="Select Your Fastener Size" v-model="values.fastenerSize" @change="updateOptions('fastenerSize')">
                                <option v-for="option in makeMenuOptions('fastenerSize')" :value="option.value" v-text="option.value" :disabled="option.disabled"></option>
                            </select>
                        </div>
                    </div>
                    <div v-if="showFastenerEmbedment && options.fastenerEmbedment && options.fastenerEmbedment.length">
                        <label for="fastener-embedment">
                            Nominal Embedment
                        </label>
                        <div class="text embedment-other" v-if="isUserDefined(values.fastener)">
                            <input type="number" min="0" id="embedment-other" step="0.01" placeholder="0.00" v-model="values.udEmbedment" @change="updateOptions('udEmbedment')">
                        </div>
                        <span v-text="useMetric(2) ? 'cm.' : 'in.'" class="measure"></span>
                        <p class="note" v-if="isUserDefined(values.fastener)">
                            Requires decimal input
                        </p>
                    </div>
                    <div v-if="showFastenerEmbedment && options.fastenerEmbedment && options.fastenerEmbedment.length">
                        <label for="fastener-embedment">
                            Effective Embedment
                        </label>
                        <div class="text embedment-other" v-if="isUserDefined(values.fastener)">
                            <input type="number" min="0" id="effective-embedment-other" step="0.01" placeholder="0.00" v-model="values.udEffectiveEmbedment" @change="updateOptions('udEffectiveEmbedment')">
                        </div>
                        <span v-text="useMetric(2) ? 'cm.' : 'in.'" class="measure"></span>
                        <p class="note" v-if="isUserDefined(values.fastener)">
                            Requires decimal input
                        </p>
                    </div>
                    <div v-if="isUserDefined(values.fastener)">
                        <label for="fastener-load">
                            Fastener Load
                        </label>
                        <div class="text embedment-other">
                            <input type="number" min="0" id="fastener-load" placeholder="0" v-model="values.udFastenerLoad" @change="updateOptions('udFastenerLoad')">
                        </div>
                        <span v-text="useMetric(2) ? 'kg.' : 'lbs.'" class="measure"></span>
                    </div>
                </fieldset>
                <fieldset v-if="showStructuralAttachmentSection" class="flex structure">
                    <div class="header">
                        <h3>Define the Structural Attachment</h3>
                        <div class="help">
                            <i class="fa fa-question inline-popup" data-mfp-src="#structural-help"></i>
                        </div>
                    </div>
                    <div v-if="options.structuralAttachment && options.structuralAttachment.length" class="structural-attachment">
                        <label for="structural-attachment">
                            Structural Attachment
                        </label>
                        <div v-if="options.structuralAttachment && options.structuralAttachment.length == 1" v-text="options.structuralAttachment[0]"></div>
                        <div v-else class="select">
                            <select id="strutual-attachment" placeholder="Structural Attachment" v-model="values.structuralAttachment" @change="updateOptions('structuralAttachment')">
                                <option v-for="option in options.structuralAttachment" :value="option" v-text="option"></option>
                            </select>
                        </div>
                    </div>
                    <div v-if="options.loadOrientation && options.loadOrientation.length">
                        <label for="load-orientation">
                            Load Orientation
                        </label>
                        <div v-if="options.loadOrientation && options.loadOrientation.length == 1" v-text="options.loadOrientation[0]"></div>
                        <div v-else class="select">
                            <select id="load-orientation" placeholder="Load Orientation" v-model="values.loadOrientation" @change="updateOptions('loadOrientation')">
                                <option v-for="option in options.loadOrientation" :value="option" v-text="option"></option>
                            </select>
                        </div>
                    </div>
                </fieldset>

                <fieldset class="flex">
                    <h3>Define the Brace Type</h3>
                    <div>
                        <label for="angle-min">
                            Brace Angle (θ)
                        </label>
                        <div v-if="angleMinimumOptions.length == 1" v-text="angleMinimumOptions[0]"></div>
                        <div v-else class="select">
                            <select id="angle-min" placeholder="Min" v-model="values.angleMin" @change="updateOptions('angleMin')">
                                <option v-for="option in makeMenuOptions('braceAngleMin')" :value="option.value" v-text="option.value" :disabled="option.disabled"></option>
                            </select>
                        </div>
                        <label for="angle-min" class="auto">
                            min
                        </label>
                        <div v-if="angleMaximumOptions.length == 1" v-text="angleMaximumOptions[0]"></div>
                        <div v-else class="select">
                            <select id="angle-max" placeholder="Max" v-model="values.angleMax" @change="updateOptions('angleMax')">
                                <option v-for="option in makeMenuOptions('braceAngleMax')" :value="option.value" v-text="option.value" :disabled="option.disabled"></option>
                            </select>
                        </div>
                        <label class="auto " for="angle-max">
                            max
                        </label>
                    </div>
                    <div>
                        <label for="lr">
                           l/r max
                        </label>
                        <div class="select">
                            <select id="lr" placeholder="L/R" v-model="values.lrMax" @change="updateOptions('lrMax')">
                                <option v-for="option in makeMenuOptions('lr')" :value="option.value" v-text="option.value" :disabled="option.disabled"></option>
                            </select>
                        </div>
                        <p class="note">
                            Slenderness ratio
                        </p>
                    </div>
                    <div>
                        <label for="brace-member">
                            Brace Member
                        </label>
                        <div class="select">
                            <select id="brace-member" placeholder="Brace Member Size" v-model="values.braceMember" @change="updateOptions('braceMember')">
                                <option v-for="option in makeMenuOptions('braceMember')" :value="option.value" v-text="option.value" :disabled="option.disabled"></option>
                            </select>
                        </div>
                    </div>
                    <div v-if="showBraceCapacityInfo">
                        <span class="note net-vertical-message" v-if="netVerticalReactionMessage" v-text="netVerticalReactionMessage"></span>
                    </div>
                    <div v-if="showBraceCapacityInfo">
                        <label>Brace Pipe Capacity</label>
                        <div v-text="lbfOrKn(fastenerBraceInfo.braceMemberCapacity, true, useMetric(2))"></div>
                    </div>
                    <div v-if="showBraceCapacityInfo">
                        <label>Brace Pipe Length</label>
                        <div v-if="useMetric(2)" v-text="ftOrM(fastenerArmLengthDecimal, true, 2)"></div>
                        <div v-else v-text="fastenerArmLength"></div>
                    </div v-if="showBraceCapacityInfo">
                    <div v-if="showBraceCapacityInfo && values.fastenerOrientation">
                        <label>Fastener Orientation</label>
                        <div v-text="fastenerBraceInfo.fastenerOrientations"></div>
                    </div>
                    <div v-if="showBraceCapacityInfo">
                        <label>Least Radius of Gyration</label>
                        <div v-text="inOrCm(fastenerBraceInfo.leastRadiusOfGyration, true, 2)"></div>
                    </div>
                </fieldset>

                <fieldset class="flex" v-if="showSwivelSection">
                    <h3>Define the Swivel Type</h3>
                    <div v-if="!hasAgency && designId" class="required" style="display: block">
                        AGENCY SELECTION REQUIRED: Please go back to
                        <router-link :to="{ name: 'submittal-setup', params: {id: designId}}">Seismic Design Setup</router-link>
                        and select an agency before proceeding for accurate options here.
                    </div>
                    <div class="radio flex">
                        <div v-for="swivel in swivelOptions">
                            <input type="radio" :id="swivel.figureNumber" name="swivel" v-model="values.swivel" :value="swivel.figureNumber" :disabled="!isValidSwivel(swivel.figureNumber)">
                            <label :for="swivel.figureNumber" :class="{'disabled':!isValidSwivel(swivel.figureNumber)}">
                                <img :src="swivel.img" :alt="swivel.figureNumber" />
                                <span v-text="swivel.figureNumber" class="radiobtn"></span>
                                <div v-if="swivelCapacity(swivel.figureNumber)" class="note">
                                    Listed Load Rating: <strong v-text="lbfOrKn(listedLoad(swivel.figureNumber), true, useMetric(2))"></strong><br>
                                    Adjusted Load Rating: <strong v-text="lbfOrKn(adjustedLoad(swivel.figureNumber), true, useMetric(2))"></strong>
                                </div>
                            </label>
                        </div>
                    </div>
                </fieldset>
            </section>
            <section v-if="structuralAttachments.length">
                <div class="attachment">
                    <fieldset class="structure-options flex structure">
                        <div class="header">
                            <h3>Choose your Structural Attachments</h3>
                            <div class="help">
                                <i class="fa fa-question inline-popup" data-mfp-src="#fastener-reference"></i>
                            </div>
                        </div>
                        <div v-if="showStructuralAttachmentSection" class="radio flex">
                            <div v-for="(attachment, index) in attachmentOptions">
                                <label :for="attachment.figureNumber" :class="{'disabled':!isValidAttachment(attachment.figureNumber)}">
                                    <img :src="attachment.img" :alt="attachment.figureNumber" />
                                    <span v-if="attachment.submittal">
                                        <modal :index="index" :result="attachment.submittal" :text="attachment.figureNumber"/>
                                    </span>
                                    <span v-else v-text="attachment.figureNumber" />
                                    <div v-if="isValidAttachment(attachment.figureNumber)" class="note">
                                        Beam Thickness:<br>
                                        <strong v-text="thickness(attachment.min)"></strong> &mdash; <strong v-text="thickness(attachment.max)"></strong>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </fieldset>
                </div>
                <div class="structural-attachments">
                    <fieldset>
                        <div class="header">
                            <div class="option">Primary</div>
                            <div v-if="showFastenerSection">
                                <a @click="sortStructuralAttachments('name')" :class="sortClass('structure', 'name')">
                                    Fastener
                                </a>
                            </div>
                            <div v-if="showFastenerSection">
                                <a @click="sortStructuralAttachments('load')" :class="sortClass('structure', 'load')">
                                    Fastener Load Capacity
                                </a>
                            </div>
                            <div v-if="showFastenerSection">
                                <a @click="sortStructuralAttachments('nominal')" :class="sortClass('structure', 'nominal')">Min Nominal Embedment</a>
                            </div>
                            <div v-if="showFastenerSection">
                                <a @click="sortStructuralAttachments('effective')" :class="sortClass('structure', 'effective')">Min Effective Embedment</a>
                            </div>
                            <div v-if="showFastenerSection">
                                Min Slab Thickness
                            </div>
                            <div v-if="showFastenerSection">
                                Min Edge Distance<div>1 | 2</div>
                            </div>
                            <div v-if="showStructuralAttachmentSection">
                                Structural Attachment, Size, Capacity
                            </div>
                            <div>
                                Swivel Connection, Size, Capacity
                            </div>
                        </div>
                        <div v-for="(attachment, index) in sortedStructuralAttachments" :class="{'disabled':attachment.disabled}">
                            <div class="option">
                                <input type="checkbox" :name="`primary-secondary-${index}`" v-model="attachment.primary" @change="choosePrimaryStructural(attachment)" :id="`attachments-primary-${index}`" :disabled="attachment.disabled == 1">
                                <label :for="`attachments-primary-${index}`"></label>
                            </div>
                            <div v-if="showFastenerSection" v-text="fastenerText(attachment)"></div>
                            <div v-if="showFastenerSection" v-text="lbfOrKn(attachment.fastenerLoadCapacity, true, useMetric(2))"></div>
                            <div v-if="showFastenerSection" v-text="embedmentText(attachment)"></div>
                            <div v-if="showFastenerSection" v-text="effectiveEmbedmentText(attachment)"></div>
                            <div v-if="showFastenerSection" v-text="displayFrac(attachment.minSlabThickness, true)"></div>
                            <div v-if="showFastenerSection" v-text="edgeDistance(attachment)"></div>
                            <div v-if="showStructuralAttachmentSection">
                                <modal :index="index" :result="attachment.structuralAttachmentSubmittal" :text="attachment.structuralAttachment"/>,
                                <span v-text="attachmentDescription(attachment)"></span>
                            </div>
                            <div>
                                <modal :index="index" :result="attachment.swivelSubmittal" :text="attachment.swivelConnection"/>,
                                {{ attachment.swivelSize }},  {{ lbfOrKn(attachment.swivelConnectionCapacity, true, useMetric(2)) }}
                            </div>
                        </div>
                    </fieldset>
                </div>
            </section>
            <p class="info section" v-if="showLoadingStructuralAttachments">
                Loading structural attachments...
            </p>
            <p class="error section" v-else-if="showStructuralAttachmentError">
                Unfortunately no structural attachments match your specifications.
            </p>

            <section v-if="pipeAttachments.length">
                <div class="pipe-attachments">
                    <h3>Choose your Pipe Attachments</h3>
                    <fieldset>
                        <div class="header">
                            <div class="option">Primary</div>
                            <div><a @click="sortPipeAttachments('name')" :class="sortClass('pipe', 'name')">Pipe Attachment</a></div>
                            <div><a @click="sortPipeAttachments('size')" :class="sortClass('pipe', 'size')">Pipe Attachment Size</a></div>
                            <div><a @click="sortPipeAttachments('capacity')" :class="sortClass('pipe', 'capacity')">Pipe Attachment Capacity</a></div>
                        </div>
                        <div v-for="(pipe, index) in sortedPipeAttachments" :class="{'disabled':pipe.disabled}">
                            <div class="option">
                                <input type="checkbox" v-model="pipe.primary" @change="choosePrimaryPipe(pipe)" :id="`pipes-primary-${index}`" :disabled="pipe.disabled == 1">
                                <label :for="`pipes-primary-${index}`"></label>
                            </div>
                            <div>
                                <modal :index="index" :result="pipe.pipeAttachmentSubmittal" :text="pipe.pipeAttachment" />
                            </div>
                            <div v-text="pipe.pipeAttachmentSize"></div>
                            <div v-text="lbfOrKn(pipe.pipeAttachmentCapacity, true, useMetric(2))"></div>
                        </div>
                    </fieldset>
                </div>
            </section>
            <p class="info section" v-if="showLoadingPipeAttachments">
                Loading pipe attachments...
            </p>
            <p class="error section" v-else-if="showPipeAttachmentError">
                Unfortunately no pipe attachments match your specifications.
            </p>

            <section>
                <actions
                    @saveProgress="saveProgress"
                    @next="saveThenNext"
                    :next-button-text="nextButtonText"
                    :saving="saving"
                ></actions>
            </section>
        </main>
        <aside class="primary">
            <sidebar :design="this.designRecord" :brace="this.braceRecord"></sidebar>
        </aside>

        <div id="fastener-help" class="inline-modal mfp-hide">
            <div>
                <img src="/img/seismic/ABC-fastener.jpg" alt="ABC fastener example">
            </div>
            <div>
                <img src="/img/seismic/DEF-fastener.jpg" alt="ABC fastener example">
            </div>
            <div>
                <img src="/img/seismic/GHI-fastener.jpg" alt="ABC fastener example">
            </div>
        </div>
        <div id="structural-help" class="inline-modal mfp-hide">
            <div>
                <img src="/img/seismic/parallel-structure.jpg" alt="Parallel structure example">
            </div>
            <div>
                <img src="/img/seismic/perpendicular-structure.jpg" alt="Perpendicular structure example">
            </div>
        </div>
        <iframe id="fastener-reference" class="inline-modal mfp-hide" src="/docs/Anchor-and-Fastener-Reference-Information.pdf" width="100%" height="500px"></iframe>
    </layout>
</template>

<script>
import ClockvineMixin from 'vue/common/clockvine-mixin';
import SubmittalDesignLoader from './mixins/submittal-design-loader';
import InputNumber from 'vue/common/_inputNumber';

import Layout from './layout';
import Sidebar from './shared/sidebar';
import Errors from './shared/errors';
import Actions from './shared/actions';
import Modal from './_modal';
import MagnificPopup from 'magnific-popup';
import Fraction from 'fraction.js';

import { mapGetters, mapState } from 'vuex';
import { mapKeyedGetters } from '../../common/vuex-extra';
import { debounce } from 'lodash';

import orderBy from 'lodash/orderBy';

export default {
    components: {
        Layout,
        Sidebar,
        Actions,
        Errors,
        InputNumber,
        Modal,
    },

    mixins: [
        ClockvineMixin,
        SubmittalDesignLoader,
    ],

    data() {
        return {
            type: 'submittalBrace',
            nextButtonText: 'Finish',
            values: {
                structure: null,
                udStructure: null,
                substrate: null,
                udSubstrate: null,
                fastener: null,
                udFastener: null,
                fastenerOrientation: null,
                fastenerSize: null,
                fastenerEmbedment: null,
                fastenerEffectiveEmbedment: null,
                udEmbedment: null,
                udEffectiveEmbedment: null,
                udFastenerLoad: null,
                structuralAttachment: null,
                loadOrientation: null,
                beamThickness: null,
                angleMin: null,
                angleMax: null,
                lrMax: null,
                braceMember: null,
                bracePipeCapacity: null,
                bracePipeLength: null,
                swivel: null,
                agency: null,
            },
            structuralAttachments: [],
            pipeAttachments: [],
            structuralAttachmentSort: null,
            pipeAttachmentSort: null,
            loadingStructuralAttachments: false,
            loadingPipeAttachments: false,
        };
    },
    watch: {
        options: {
            handler() {
                let shouldRecurse = false;

                this.fields.forEach(f => {
                    if (this.options[f] && !this.options[f].includes(this.values[f])) {
                        this.values[f] = null;
                    }

                    // If there's option one option, set it to that.
                    if (this.options[f] && this.options[f].length == 1) {
                        // If this option wasn't previously set, make sure we re-updateOptions
                        if (this.values[f] == null && this.options[f][0] != null) {
                            shouldRecurse = true;
                        }

                        this.values[f] = this.options[f][0];
                    }
                });

                // If at least one value is set now that wasn't before, re-updateOptions
                if (shouldRecurse) {
                    this.updateOptions();
                }

                if (this.angleMinimumOptions.length == 1) {
                    this.values.angleMin = this.angleMinimumOptions[0];
                }

                if (this.angleMaximumOptions.length == 1) {
                    this.values.angleMax = this.angleMaximumOptions[0];
                }
            },
            deep: true
        },
        "values.angleMin"() {
            this.$store.dispatch('submittalAttachment/fetchNetVerticalReactionMessage', {
                angleMin: this.values.angleMin,
            });
        },
        "values.swivel"() {
            this.updateOptions('swivel');
        },
        record() {
            this.init();
        },
        structuralAttachmentOptions: {
            handler() {
                // This bit of madness is to preserve which options are checked
                // when coming to this page via navigation.
                for (let i = 0; i < this.structuralAttachments.length; i++) {
                    if (i < this.structuralAttachmentOptions.length
                        && this.structuralAttachmentsAreEqual(this.structuralAttachments[i], this.structuralAttachmentOptions[i])) {
                        this.structuralAttachmentOptions[i].primary = this.structuralAttachments[i].primary;
                    }
                }

                this.structuralAttachments = this.structuralAttachmentOptions;
                this.record.structuralAttachmentOptions = this.structuralAttachmentOptions;
            },
            deep: true,
        },
        pipeAttachmentOptions: {
            handler() {
                // This bit of madness is to preserve which options are checked
                // when coming to this page via navigation.
                for (let i = 0; i < this.pipeAttachments.length; i++) {
                    if (i < this.pipeAttachmentOptions.length
                        && this.pipeAttachmentsAreEqual(this.pipeAttachments[i], this.pipeAttachmentOptions[i])) {
                        this.pipeAttachmentOptions[i].primary = this.pipeAttachments[i].primary;
                    }
                }

                this.pipeAttachments = this.pipeAttachmentOptions;
                this.record.pipeAttachmentOptions = this.pipeAttachmentOptions;
            },
            deep: true,
        },
        standardId() {
            this.fetchFormOptions();
        },
        braceId() {
            this.fetchFormOptions();
        },
    },
    computed: {
        cp() {
            // this method is for the sidebar component
            return this.record.cp;
        },
        name() {
            return this.record.name;
        },
        drawingReference() {
            return this.record.drawingReference;
        },
        braceRecord() {
            return this.record;
        },
        substrateOptions() {
            return this.makeMenuOptions('substrate');
        },
        ...mapGetters({
            standardId: 'submittalStandards/standardId',
            standardName: 'submittalStandards/standardName',
            menuOptions: 'submittalAttachment/getMenuOptions',
            zoiSeismicDesignLoad: 'submittalSystem/zoiSeismicDesignLoad',
            braceId: 'submittalSystem/braceId',
        }),
        ...mapState({
            "fields": state => state.submittalAttachment.fields,
            "options": state => state.submittalAttachment.options,
            "fastenerBraceInfo": state => state.submittalAttachment.fastenerBraceInfo,
            "structuralAttachmentOptions": state => state.submittalAttachment.structuralAttachmentOptions,
            "pipeAttachmentOptions": state => state.submittalAttachment.pipeAttachmentOptions,
            "netVerticalReactionMessage": state => state.submittalAttachment.netVerticalReactionMessage,
            "braceMembers": state => state.submittalAttachment.braceMembers,
        }),
        fastenerOptions() {
            return this.makeMenuOptions('fastener').filter(a => a.disabled == false);
        },
        fastenerOrientationOptions() {
            return this.makeMenuOptions('fastenerOrientation').filter(a => a.disabled == false);
        },
        fastenerSizeOptions() {
            return this.makeMenuOptions('fastenerSize').filter(a => a.disabled == false);
        },
        fastenerArmLength() {
            return `${this.fastenerBraceInfo.braceMemberLength.lengthFt}'${this.fastenerBraceInfo.braceMemberLength.lengthIn}"`;
        },
        fastenerArmLengthDecimal() {
            return this.fastenerBraceInfo.braceMemberLength.lengthFt + this.fastenerBraceInfo.braceMemberLength.lengthIn / 12;
        },
        showSwivelSection() {
            return true;
            // Commenting this logic out until I'm sure ASC won't ask to put it back in - BB 2023-06-07
            // if (this.values.structure == 'Cracked Concrete') {
            //     return true;
            // }
            // return false;
        },
        swivelOptions() {
            let priority = ['AF700'];
            let options = [];
            let c = 0;
            let i = 0;

            // Add the priority items first
            for (i = 0; i < priority.length; i++) {
                for (c = 0; c < this.menuOptions.swivels.length; c++) {
                    if (priority[i] == this.menuOptions.swivels[c].figureNumber) {
                        options.push(this.menuOptions.swivels[c]);
                    }
                }
            }
            // Add the non-priority items after
            for (c = 0; c < this.menuOptions.swivels.length; c++) {
                for (i = 0; i < priority.length; i++) {
                    if (priority[i] != this.menuOptions.swivels[c].figureNumber) {
                        options.push(this.menuOptions.swivels[c]);
                    }
                }
            }

            return options;
        },
        showSubstrateSection() {
            if (this.isZPurlin || this.isUserDefined(this.values.structure)) {
                return true;
            }
            if (this.options.substrate && this.options.substrate.length && this.substrateOptions.length) {
                return true;
            }
            return false;
        },
        showFastenerSection() {
            return (['Cracked Concrete','Wood (Lag Bolt)', 'Lumber/Timbers (Thru Bolt)', 'Composite Wood Joist', 'Steel', 'Z-Purlin'].includes(this.values.structure) || this.isUserDefined(this.values.structure));
        },
        showFastenerEmbedment() {
            return this.isUserDefined(this.values.structure);
        },
        showStructuralAttachmentSection() {
            return ['I-Beam/Joist'].includes(this.values.structure);
        },
        attachmentOptions() {
            let options = [];
            let c = 0;

            for (c = 0; c < this.menuOptions.attachments.length; c++) {
                options.push(this.menuOptions.attachments[c]);
            }

            return options;
        },
        angleMinimumOptions() {
            let values = [];
            if (this.record.orientation == 'riser-wall') {
                values = [30, 45];
            } else {
                values = [30, 45, 60];
            }

            return values
        },
        angleMaximumOptions() {
            let options = [];
            if (this.record.orientation == 'riser-wall') {
                options = [44, 59];
            } else {
                options = [44, 59, 90];
            }

            return options.filter(a => a > this.values.angleMin);
        },
        braceValuesFilled() {
            let check = (this.values.structure
                && this.values.substrate
                && this.values.angleMin
                && this.values.angleMax
                && this.values.lrMax);

            if (this.showFastenerSection) {
                check = (check
                    && this.values.fastener
                    && this.values.fastenerOrientation
                    && this.values.fastenerSize);

                if (this.isUserDefined(this.values.structure)) {
                    check = (check && this.values.udFastenerLoad);
                }
            }

            if (this.showStructuralAttachmentSection) {
                check = (check
                    && this.values.structuralAttachment
                    && this.values.loadOrientation);
            }

            return check;
        },
        allValuesFilled() {
            let check = (this.braceValuesFilled && this.values.swivel && this.values.agency);

            return check;
        },
        showBraceCapacityInfo() {
            if (this.braceValuesFilled && this.fastenerBraceInfo.braceMemberCapacity) {
                return true;
            }
            return false;
        },
        showLoadingStructuralAttachments() {
            if (this.allValuesFilled && this.loadingStructuralAttachments) {
                return true;
            }

            return false;
        },
        showLoadingPipeAttachments() {
            if (this.allValuesFilled && this.loadingPipeAttachments) {
                return true;
            }

            return false;
        },
        showStructuralAttachmentError() {
            if (this.showLoadingStructuralAttachments) {
                return false;
            }
            if (this.allValuesFilled) {
                 if (this.structuralAttachments.length == 0) {
                    return true;
                }

                if (this.structuralAttachments.filter(a => a.disabled == false).length == 0) {
                    return true;
                }
            }

            return false;
        },
        showPipeAttachmentError() {
            if (this.showLoadingPipeAttachments) {
                return false;
            }
            if (this.allValuesFilled) {
                if (this.pipeAttachments.length == 0) {
                    return true;
                }

                if (this.pipeAttachments.filter(a => a.disabled == false).length == 0) {
                    return true;
                }
            }

            return false;
        },
        sortedStructuralAttachments() {
            if (this.structuralAttachmentSort) {
                let dir = this.structuralAttachmentSort.direction;

                let capSort = function (o) { return new Number(o.fastenerLoadCapacity); }
                let nominalEmbed = function (o) { return new Number(o.fastenerEmbedment); }
                let effectiveEmbed = function (o) { return new Number(o.fastenerEffectiveEmbedment); }

                switch (this.structuralAttachmentSort.column) {
                    case 'name':
                        return orderBy(this.structuralAttachments, ['name', capSort], [dir, 'asc']);
                    case 'load':
                        return orderBy(this.structuralAttachments, [capSort, 'name'], [dir, 'asc']);
                    case 'nominal':
                        return orderBy(this.structuralAttachments, [nominalEmbed, 'name'], [dir, 'asc']);
                    case 'effective':
                        return orderBy(this.structuralAttachments, [effectiveEmbed, 'name'], [dir, 'asc']);
                    default:
                        return this.structuralAttachments;
                }
            } else {
                return this.structuralAttachments;
            }
        },
        sortedPipeAttachments() {
            if (this.pipeAttachmentSort) {
                let dir = this.pipeAttachmentSort.direction;

                let capSort = function (o) { return new Number(o.pipeAttachmentCapacity); }

                switch (this.pipeAttachmentSort.column) {
                    case 'name':
                        return orderBy(this.pipeAttachments, ['pipeAttachment', capSort], [dir, 'asc']);
                    case 'capacity':
                        return orderBy(this.pipeAttachments, [capSort, 'pipeAttachment'], [dir, 'asc']);
                    case 'size':
                        return orderBy(this.pipeAttachments, ['pipeAttachmentSize', 'pipeAttachment'], [dir, 'asc']);
                    default:
                        return this.structuralAttachments;
                }
            } else {
                return this.pipeAttachments;
            }
        },
        hasAgency() {
            return this.designRecord ? this.designRecord.agency : '';
        },
        // designId() {
        //     return this.record.designId;
        // },
    },
    methods: {
        init: debounce(function() {
            // Developer's note, this.values array never really got set properly here and I have no idea why - Bill B 08-03-2021
            Object.entries(this.values).forEach(([k, v]) => {
                this.values[k] = this.record[k];
            });

            if (this.designRecord && !this.values.agency) {
                this.values.agency = this.designRecord.agency;
            }

            this.$store.dispatch('submittalAttachment/setValues', this.values).catch(() => {})
                .then(() => {
                    this.$nextTick(() => {
                        var $popup = $('.inline-popup');
                        if ($popup.length) {
                            $popup.magnificPopup({
                                type:'inline'
                            });
                        }
                    });
                });

            let structuralAttachmentOptions = this.record.structuralAttachmentOptions;
            let pipeAttachmentOptions = this.record.pipeAttachmentOptions;

            if (structuralAttachmentOptions && structuralAttachmentOptions.length) {
                this.$store.commit('submittalAttachment/setStructuralAttachmentOptions', structuralAttachmentOptions);
            } else {
                this.$store.dispatch('submittalAttachment/fetchStructuralAttachmentOptions').catch(() => {});
            }

            if (pipeAttachmentOptions && pipeAttachmentOptions.length) {
                this.$store.commit('submittalAttachment/setPipeAttachmentOptions', pipeAttachmentOptions);
            } else {
                this.fetchPipeAttachmentOptions();
            }

            this.$store.dispatch('submittalAttachment/fetchFastenerBraceInfo').catch(() => {});
            this.$store.dispatch('submittalAttachment/fetchBraceMembers')
                .then(() => {
                    this.validateBraceMember();
                })
                .catch(() => {});

        }, 500, {'leading': false}),
        fetchFormOptions() {
            if (this.standardId && this.braceId) {
                this.$store.dispatch('submittalAttachment/fetchFormOptions')
                    .then(() => {
                        return this.$store.dispatch('submittalSystem/fetchAll', { id: this.id });
                    }, (err) => {
                        console.error('Failed fetchFormOptions ' + err);
                    })
                    .then(() => {
                        this.init();
                        this.$store.dispatch('submittalAttachment/fetchNetVerticalReactionMessage', {
                            angleMin: this.values.angleMin,
                        });
                    }, (err) => {
                        console.error('Failed fetchAll ' + err);
                    });
            }
        },
        fetchPipeAttachmentOptions() {
            let attachmentComponent = this
            this.loadingPipeAttachments = true;

            this.$store.dispatch('submittalAttachment/fetchPipeAttachmentOptions').catch(() => {})
                .then(() => attachmentComponent.loadingPipeAttachments = false)
                .then(() => {
                    if (attachmentComponent.pipeAttachments.length == 0) {
                        return;
                    }

                    let active = attachmentComponent.pipeAttachments.filter(a => a.disabled == false);

                    if (active.length == 1) {
                        active[0].primary = true;
                        attachmentComponent.choosePrimaryPipe(active[0]);
                    }
                })
                .catch(() => attachmentComponent.loadingPipeAttachments = false);
        },
        isValidValue(field, value) {
            if (!this.options[field]) {
                return false;
            }
            return this.options[field].includes(value);
        },
        isValidAttachment(figureNumber) {
            if (!this.isValidValue('structuralAttachmentFigure', figureNumber)) {
                return false;
            }
            return true;
        },
        isValidSwivel(figureNumber) {
            if (!this.isValidValue('swivel', figureNumber)) {
                return false;
            }
            if (this.swivelCapacity(figureNumber)) {
                return true;
            }
            return false;
        },
        updateOptions(columnChanged) {
            let fields = Object.keys(this.values);

            for (let i = 0; i < fields.length; i++) {
                let field = fields[i];
                if (this.options[field] && !this.isValidValue(field, this.values[field])) {
                    this.values[field] = null;
                }
            }
            // Set the default swivel
            if (columnChanged == 'structure') {
                if (this.showSwivelSection) {
                    if (this.values.swivel == null && this.record.swivel == null) {
                        this.values.swivel = 'AF700';
                    }
                }  else {
                    this.values.swivel = null;
                }
            }
            this.$store.dispatch('submittalAttachment/setValues', this.values).then(() => {
                this.$nextTick(() => {
                    var $popup = $('.inline-popup');
                    if ($popup.length) {
                        $popup.magnificPopup({
                            type:'inline'
                        });
                    }
                });
            })
            .catch(() => {})
            .then(() => {
                this.loadingStructuralAttachments = true;
                this.$store.dispatch('submittalAttachment/fetchFastenerBraceInfo').catch(() => {});
                this.$store.dispatch('submittalAttachment/fetchBraceMembers').catch(() => {});
                this.$store.dispatch('submittalAttachment/fetchStructuralAttachmentOptions').catch(() => {}).then(() => this.loadingStructuralAttachments = false);
                this.fetchPipeAttachmentOptions();
            })
            .then(() => {
                this.setDefaultSubstrate();
            });
        },

        setDefaultSubstrate() {
            // If the structure is null OR the substrate is already set, skip this step
            if (this.values.structure === null) {
                return;
            }
            if (this.values.substrate !== null) {
                return;
            }

            let substrate = null;

            if (this.values.structure == 'Wood (Lag Bolt)' || this.values.structure == 'Lumber/Timbers (Thru Bolt)') {
                substrate = this.menuOptions.substrates[this.values.structure][0];
            }
            this.values.substrate = substrate;

            if (substrate) {
                this.updateOptions('substrate');
            }
        },

        updateStructuralAttachments() {
            this.$store.commit('submittalAttachment/setStructuralAttachmentOptions', this.structuralAttachments);
        },

        updatePipeAttachments() {
            this.$store.commit('submittalAttachment/setPipeAttachmentOptions', this.pipeAttachments);
        },

        saveProgress() {
            this.saving = true;

            if (this.structuralAttachments.findIndex(i => i.primary) !== -1 && this.pipeAttachments.findIndex(i => i.primary) !== -1) {
                this.record.needsReview = false;
            } else {
                this.record.needsReview = true;
            }

            return this.save();
        },

        saveThenNext() {
            this.record.needsReview = false;
            this.save().then(() => {
                this.$router.push({name: 'submittal-brace-summary', params: {
                    id: this.designId
                }});
            });
        },

        getRecord() {
            return {...this.record, ...this.values};
        },

        getUrlParams() {
            return { designId: this.$route.params.designId };
        },

        getRecordParams() {
            let record = Object.assign({},
                this.record,
                this.values,
                {
                    bracePipeCapacity: this.fastenerBraceInfo.braceMemberCapacity,
                    bracePipeLength: this.fastenerBraceInfo.braceMemberLength,
                    leastRadiusOfGyration: this.fastenerBraceInfo.leastRadiusOfGyration,
                    calculatedFastenerOrientation: this.fastenerBraceInfo.fastenerOrientations
                }
            );

            return Object.assign({}, this.getUrlParams(), { data: record });
        },

        choosePrimaryStructural(attachment) {
            if (attachment.primary) {
                this.structuralAttachments.forEach(attachment => attachment.primary = false);
                attachment.primary = true;
            }

            this.updateStructuralAttachments();
        },

        choosePrimaryPipe(attachment) {
            if (attachment.disabled) {
                return;
            }
            if (attachment.primary) {
                this.pipeAttachments.forEach(attachment => attachment.primary = false);
                attachment.primary = true;
            }

            this.updatePipeAttachments();
        },

        toFrac: n => (new Fraction(n)).toFraction(true),

        structuralAttachmentsAreEqual(one, two) {
            let attrs = [
                'fastener', 'fastenerLoadCapacity', 'structuralAttachment', 'structuralAttachmentCapacity', 'structuralAttachmentSize', 'swivelConnection', 'swivelConnectionCapacity', 'swivelOrientiation', 'swivelSize'
            ];
            for (let attr of attrs) {
                if (one[attr] != two[attr]) {
                    return false;
                }
            }

            return true;
        },
        pipeAttachmentsAreEqual(one, two) {
            let attrs = ['pipeAttachment', 'pipeAttachmentCapacity', 'pipeAttachmentSize'];
            for (let attr of attrs) {
                if (one[attr] != two[attr]) {
                    return false;
                }
            }

            return true;
        },
        sortStructuralAttachments(column) {
            let direction = 'asc';

            if (this.structuralAttachmentSort) {
                if (this.structuralAttachmentSort.column == column) {
                    direction = this.structuralAttachmentSort.direction == 'asc' ? 'desc' : 'asc';
                }
            }

            this.structuralAttachmentSort = {column, direction}
        },
        sortPipeAttachments(column) {
            let direction = 'asc';

            if (this.pipeAttachmentSort) {
                if (this.pipeAttachmentSort.column == column) {
                    direction = this.pipeAttachmentSort.direction == 'asc' ? 'desc' : 'asc';
                }
            }

            this.pipeAttachmentSort = {column, direction}
        },
        sortClass(type, column) {
            if ('structure' === type) {
                if (this.structuralAttachmentSort) {
                    return this.structuralAttachmentSort.column == column ? this.structuralAttachmentSort.direction + ' active' : 'asc';
                }
                return 'asc';
            }
            if ('pipe' === type) {
                if (this.pipeAttachmentSort) {
                    return this.pipeAttachmentSort.column == column ? this.pipeAttachmentSort.direction + ' active' : 'asc';
                }
                return 'asc';
            }
        },
        makeMenuOptions(field) {
            let key = '';

            switch (field) {
                case 'structure':
                    return this.makeMenu(this.menuOptions.structures, this.options.structure);
                case 'substrate':
                    key = this.values.structure;
                    if (key && this.menuOptions.substrates[key]) {
                        return this.makeMenu(this.menuOptions.substrates[key], this.options.substrate);
                    }
                case 'fastener':
                    key = this.values.structure;
                    if (key && this.menuOptions.fasteners[key]) {
                        return this.makeMenu(this.menuOptions.fasteners[key], this.options.fastener);
                    }
                case 'fastenerOrientation':
                    key = this.values.fastener;
                    if (this.menuOptions.fastenerOrientations) {
                        return this.makeMenu(this.menuOptions.fastenerOrientations, this.options.fastenerOrientation);
                    }
                case 'fastenerSize':
                    key = this.values.fastenerOrientation;
                    if (key && this.menuOptions.fastenerSizes[key]) {
                        return this.makeMenu(this.menuOptions.fastenerSizes[key], this.options.fastenerSize);
                    }
                case 'fastenerEmbedment':
                    key = this.values.substrate;
                    if (key && this.menuOptions.fastenerEmbedments[key]) {
                        return this.makeMenu(this.menuOptions.fastenerEmbedments[key], this.options.fastenerEmbedment);
                    }
                case 'braceMember':
                    let allMembers = this.menuOptions.braceMembers;

                    if (allMembers) {
                        let isDisabled = false;
                        let options = [];

                        let validMembers = this.braceMembers;

                        allMembers.forEach(function(option) {
                            isDisabled = false;

                            if (validMembers && !validMembers.includes(option.name)) {
                                isDisabled = true;
                            }

                            options.push({value:option.name, disabled: isDisabled});
                        });

                        return options;
                    }
                case 'braceAngleMin':
                    let validMinAngles = [];

                    if (this.fastenerBraceInfo.fastenerAngleLoads) {
                        if (this.zoiSeismicDesignLoad <= parseInt(this.fastenerBraceInfo.fastenerAngleLoads['30-44'])) {
                            validMinAngles.push(30);
                        }
                        if (this.zoiSeismicDesignLoad <= parseInt(this.fastenerBraceInfo.fastenerAngleLoads['45-59'])) {
                            validMinAngles.push(45);
                        }
                        if (this.zoiSeismicDesignLoad <= parseInt(this.fastenerBraceInfo.fastenerAngleLoads['60-90'])) {
                            validMinAngles.push(60);
                        }
                    }

                    return this.makeMenu(this.angleMinimumOptions, validMinAngles);
                case 'braceAngleMax':
                    let validMaxAngles = [];

                    if (this.fastenerBraceInfo.fastenerAngleLoads) {
                        if (this.zoiSeismicDesignLoad <= parseInt(this.fastenerBraceInfo.fastenerAngleLoads['30-44'])) {
                            validMaxAngles.push(44);
                        }
                        if (this.zoiSeismicDesignLoad <= parseInt(this.fastenerBraceInfo.fastenerAngleLoads['45-59'])) {
                            validMaxAngles.push(59);
                        }
                        if (this.zoiSeismicDesignLoad <= parseInt(this.fastenerBraceInfo.fastenerAngleLoads['60-90'])) {
                            validMaxAngles.push(90);
                        }
                    }

                    return this.makeMenu(this.angleMaximumOptions, validMaxAngles);
                case 'lr':
                    let allLR = ['100', '200', '300'];
                    let validLR = [];
                    let test = null;

                    if (this.fastenerBraceInfo.lrCapacity) {
                        let designLoad = this.zoiSeismicDesignLoad;
                        let capacities = this.fastenerBraceInfo.lrCapacity;

                        allLR.forEach(function(lr, index){
                            if (designLoad <= parseInt(capacities[lr])) {
                                validLR.push(lr);
                            }
                        });
                    }

                    return this.makeMenu(allLR, validLR);
                default:
                    return [];
            }
        },
        makeMenu(allValues, validValues) {
            let disabled = false;
            let options = [];

            allValues.forEach(function(value) {
                disabled = false;

                if (validValues.length && !validValues.includes(value)) {
                    disabled = true;
                }

                options.push({'value':value, 'disabled': disabled});
            });

            return options;
        },
        fastenerText(attachment) {
            if (this.isUserDefined(this.values.structure)) {
                return this.values.udFastener;
            } else {
                return attachment.name;
            }
        },
        embedmentText(attachment) {
            if (this.isUserDefined(this.values.structure)) {
                return this.displayFrac(attachment.udEmbedment);
            } else {
                return this.displayFrac(attachment.fastenerEmbedment);
            }
        },
        effectiveEmbedmentText(attachment) {
            if (this.isUserDefined(this.values.structure)) {
                return this.displayFrac(attachment.udEffectiveEmbedment);
            } else {
                return this.displayFrac(attachment.fastenerEffectiveEmbedment);
            }
        },
        displayFrac(depth) {
            let decimal = parseFloat(depth);

            if (!decimal) {
                return '--';
            }

            if (this.useMetric(2)) {
                return this.inOrCm(decimal, true, 2);
            }

            let frac = this.toFrac(decimal);

            return this.inOrCm(frac, true, 2);
        },
        edgeDistance(attachment) {
            let distances = [
                this.displayFrac(attachment.minEdgeDistance1, true),
                this.displayFrac(attachment.minEdgeDistance2, true)
            ];

            return distances.join(' | ');
        },
        swivelCapacity(figureNumber) {
            if (!this.fastenerBraceInfo || !this.fastenerBraceInfo.swivelLoads) {
                return null;
            }
            if (typeof this.fastenerBraceInfo.swivelLoads[figureNumber] === "undefined") {
                return null;
            }
            return this.fastenerBraceInfo.swivelLoads[figureNumber];
        },
        listedLoad(figureNumber) {
            let capacity = this.swivelCapacity(figureNumber);

            if (capacity) {
                return capacity.listedLoad;
            }

            return null;
        },
        adjustedLoad(figureNumber) {
            let capacity = this.swivelCapacity(figureNumber);

            if (capacity) {
                return capacity.adjustedLoad;
            }

            return null;
        },
        validateBraceMember() {
            if (this.braceMembers && this.braceMembers.length > 0) {
                if (!this.braceMembers.includes(this.values.braceMember)) {
                    this.values.braceMember = null;
                    this.updateStructuralAttachments();
                    this.updatePipeAttachments();
                }
            }
        },
        attachmentDescription(a) {
            let d = ''

            if (a.beamThicknessMin && a.beamThicknessMax) {
                let min = Number.parseFloat(a.beamThicknessMin).toFixed(2);
                let max = Number.parseFloat(a.beamThicknessMax).toFixed(2);

                d = d  + this.inOrCm(min, false, this.useMetric(2)) + '-' + this.inOrCm(max, true, this.useMetric(2));
            } else if (a.structuralAttachmentSize) {
                d = d + a.structuralAttachmentSize;
            }
            if (a.structuralAttachmentCapacity) {
                if (d) {
                    d = d + ', ';
                }
                d = d + this.lbfOrKn(a.structuralAttachmentCapacity, true, this.useMetric(2));
            }
            return d;
        },
        thickness(v) {
            let value = parseFloat(v).toFixed(3);

            return this.inOrCm(value, true, 2)
        },
    },

    mounted() {
        if (!this.braceId) {
            // Reload the submittal-system store (empty on hard page refresh)
            this.$store.dispatch('submittalSystem/fetchAll', { id: this.$route.params.id });
        }
        this.fetchFormOptions();

        var $popup = $('.inline-popup');
        if ($popup.length) {
            $popup.magnificPopup({
                type:'inline'
            });
        }
    },
}
</script>